/**
 * Layer utility functions.
 * Provides functions for creating and modifying layers.
 */

import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs } from "../core/oda";
import { OpenMode } from "../core/constants";
import { parseRGBColor, isRGBColor } from "../types/NoteConfig";
import { resolveLinetype } from "../linetype/utils";
import {
  getColorInfoAndDelete,
  isForegroundRgb,
  setOdColorRgb,
} from "../colors/color";

/**
 * Create a layer with the given configuration.
 * Returns true if layer was created or already exists.
 */
export function ensureLayerExists(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  layerName: string,
  color?: string,
  lineWeight?: number,
  linetype?: string,
): boolean {
  const layerTableId = db.getLayerTableId();
  const layerTable = openAs(
    layerTableId,
    module.OdDbLayerTable,
    OpenMode.kForWrite,
  );

  deleteObj(layerTableId);

  const layerNameStr = new module.OdString();
  layerNameStr.set(layerName);

  if (!layerTable.has1(layerNameStr)) {
    // Create new layer
    const newLayer = module.OdDbLayerTableRecord.createObject();
    newLayer.setName(layerNameStr);

    // Set layer color if provided
    if (color && isRGBColor(color)) {
      const colorObj = new module.OdCmColor();
      setOdColorRgb(colorObj, parseRGBColor(color));
      newLayer.setColor(colorObj);
      deleteObj(colorObj);
    }

    // Set layer line weight if provided
    if (lineWeight !== undefined) {
      newLayer.setLineWeight(lineWeight);
    }

    // Set layer linetype if provided
    if (linetype) {
      newLayer.setLinetypeObjectId(resolveLinetype(module, db, linetype));
    }

    layerTable.add(newLayer);
    deleteObj(newLayer);
  }

  deleteObj(layerNameStr);
  deleteObj(layerTable);

  return true;
}

export function updateLayer(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  layerName: string,
  color?: string,
  lineWeight?: number,
  linetype?: string,
): boolean {
  const layerTableId = db.getLayerTableId();
  const layerTable = openAs(
    layerTableId,
    module.OdDbLayerTable,
    OpenMode.kForRead,
  );
  deleteObj(layerTableId);

  const layerNameStr = new module.OdString();
  layerNameStr.set(layerName);

  if (!layerTable.has1(layerNameStr)) {
    deleteObj(layerNameStr);
    deleteObj(layerTable);
    return false;
  }

  const layerId = layerTable.getAt3(layerNameStr, false);
  deleteObj(layerNameStr);
  deleteObj(layerTable);

  const layer = openAs(
    layerId,
    module.OdDbLayerTableRecord,
    OpenMode.kForWrite,
  );

  if (color !== undefined && isRGBColor(color)) {
    const colorObj = new module.OdCmColor();
    setOdColorRgb(colorObj, parseRGBColor(color));
    layer.setColor(colorObj);
    deleteObj(colorObj);
  }

  if (lineWeight !== undefined) {
    layer.setLineWeight(lineWeight);
  }

  if (linetype) {
    layer.setLinetypeObjectId(resolveLinetype(module, db, linetype));
  }

  deleteObj(layer);
  return true;
}

/**
 * Set the color of a layer by name.
 */
export function setLayerColor(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  layerName: string,
  color: string,
): boolean {
  const layerTableId = db.getLayerTableId();
  const layerTable = openAs(
    layerTableId,
    module.OdDbLayerTable,
    OpenMode.kForRead,
  );

  const layerNameStr = new module.OdString();
  layerNameStr.set(layerName);

  if (!layerTable.has1(layerNameStr)) {
    deleteObj(layerNameStr);
    deleteObj(layerTable);
    return false;
  }

  const layerId = layerTable.getAt3(layerNameStr, false);
  deleteObj(layerNameStr);
  deleteObj(layerTable);

  const layer = openAs(
    layerId,
    module.OdDbLayerTableRecord,
    OpenMode.kForWrite,
  );

  if (isRGBColor(color)) {
    const colorObj = new module.OdCmColor();
    setOdColorRgb(colorObj, parseRGBColor(color));
    layer.setColor(colorObj);
    deleteObj(colorObj);
  }

  deleteObj(layer);
  return true;
}

/**
 * Plan background changed: a layer still stored in TrueColor pure black or
 * white (written before ACI 7 was used for them, cf. setOdColorRgb) moves to
 * ACI 7 so it, and its ByLayer strokes, follow the new background. Any other
 * color (already ACI 7, customized in AutoCAD, client layer…) is left as is.
 * @returns true if the layer was migrated
 */
export function migrateForegroundLayerColor(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  layerName: string,
): boolean {
  const layerTableId = db.getLayerTableId();
  const layerTable = openAs(
    layerTableId,
    module.OdDbLayerTable,
    OpenMode.kForRead,
  );
  deleteObj(layerTableId);

  const layerNameStr = new module.OdString();
  layerNameStr.set(layerName);
  const exists = layerTable.has1(layerNameStr);
  const layerId = exists ? layerTable.getAt3(layerNameStr, false) : null;
  deleteObj(layerNameStr);
  deleteObj(layerTable);
  if (!layerId) return false;

  const layer = openAs(layerId, module.OdDbLayerTableRecord, OpenMode.kForRead);
  const { rgb } = getColorInfoAndDelete(layer.color());
  deleteObj(layer);
  if (!rgb || !isForegroundRgb(rgb)) return false;

  const writable = openAs(
    layerId,
    module.OdDbLayerTableRecord,
    OpenMode.kForWrite,
  );
  const colorObj = new module.OdCmColor();
  setOdColorRgb(colorObj, rgb);
  writable.setColor(colorObj);
  deleteObj(colorObj);
  deleteObj(writable);
  return true;
}
